class AriaCombobox extends HTMLElement {
  constructor() {
    super();

    this.swipeClose = this.querySelector("[data-swipe-close]");

    this.handleToggle = (e) => {
      if (e.detail.id !== this.id) return;
      this.opener = e.target;
      this._toggle();
    };

    this.closeEsc = (e) => {
      if (e.key === "Escape") {
        this._close();
      }
    };

    document.addEventListener("click", (e) => {
      if (this.contains(e.target) || this.opener?.contains(e.target)) return;
      this._close();
    });

    document.addEventListener("focusout", (e) => {
      if (
        !e.relatedTarget ||
        this.contains(e.relatedTarget) ||
        this.opener?.contains(e.relatedTarget)
      )
        return;

      this._close();
    });

    this.addEventListener("aria-listbox:focus-change", (e) => {
      if (!this.opener) return;

      this.opener.setAttribute("aria-activedescendant", e.target.id);
    });

    this.handleChange = () => {
      if (this._closeOnChange) {
        // Brief delay so user can register their change before drawer closes
        setTimeout(() => {
          this._close();
        }, 250);
      }
    };

    if (this.swipeClose) {
      this.swipeClose.addEventListener("click", () => {
        if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
          this._close();
        }
      });

      this.swipeClose.addEventListener("touchstart", (e) => {
        this.touchstart = e.targetTouches[0].clientY;
        this.touchend = null;

        console.log(`this.touchStart`, this.touchStart);
      });

      this.addEventListener("touchmove", (e) => {
        if (!this.touchstart) return;

        this.touchend = e.targetTouches[0].clientY;
        this._setSwipeOffset(Math.max(0, this.touchend - this.touchstart));
      });

      this.addEventListener("touchend", () => {
        if (this.touchend && this.touchend - this.touchstart > 50) {
          this._close();
        } else {
          this._setSwipeOffset(0);
        }
      });
    }

    this.addEventListener("change", this.handleChange);
    this.addEventListener("aria-listbox:change", this.handleChange);
  }

  connectedCallback() {
    document.addEventListener("aria-combobox:toggle", this.handleToggle);
    document.addEventListener("keydown", this.closeEsc);
  }

  disconnectedCallback() {
    document.removeEventListener("aria-combobox:toggle", this.handleToggle);
    document.removeEventListener("keydown", this.closeEsc);
  }

  _toggle() {
    if (this.hasAttribute("open")) {
      this._close();
    } else {
      this._open();
    }
  }

  _open() {
    if (this.isOpen) return;
    this.removeEventListener("transitionend", this.onEnd);
    this.isOpen = true;
    this.onEnd = () => {
      this.removeEventListener("transitionend", this.onEnd);
      this.querySelector("aria-listbox-v2").setAttribute("active", "");
    };

    this.addEventListener("transitionend", this.onEnd);

    this.opener.setAttribute("aria-expanded", "true");
    this.style.display = null;

    if (this._isModal) {
      window.openFullDimmer(this);
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.querySelector("[data-swipe-close]"));
      this.focusIsTrapped = true;
    }

    window.requestAnimationFrame(() => {
      this.setAttribute("open", "");
    });
  }

  _close() {
    if (!this.isOpen) return;
    this.removeEventListener("transitionend", this.onEnd);

    this.onEnd = () => {
      this.removeEventListener("transitionend", this.onEnd);
      this.querySelector("aria-listbox-v2").removeAttribute("active");
      this.style.display = "none";
      this.isOpen = false;
      this.touchstart = null;
      this.touchend = null;
      this._setSwipeOffset(0);
      if (this.focusIsTrapped) {
        window.removeTrapFocus(this.returnFocusEl);
        this.focusIsTrapped = false;
      }

      window.unlockScroll(this);
    };

    this.addEventListener("transitionend", this.onEnd);

    this.removeAttribute("open");
    this.opener.setAttribute("aria-expanded", "false");
    window.closeFullDimmer(this);
  }

  get _closeOnChange() {
    const closeOnChangeWhenQuery =
      this.getAttribute("close-on-change-when") || null;

    if (!closeOnChangeWhenQuery) return false;

    return window.matchMedia(closeOnChangeWhenQuery).matches;
  }

  get _isModal() {
    const isModalWhenQuery = this.getAttribute("is-modal-when") || null;

    if (!isModalWhenQuery) return false;

    return window.matchMedia(isModalWhenQuery).matches;
  }

  _setSwipeOffset(px) {
    this.style.setProperty("--swipe-offset", `${isFinite(px) ? px : 0}px`);
  }
}

if (!customElements.get("aria-combobox")) {
  customElements.define("aria-combobox", AriaCombobox);
}
